<script setup lang="ts">
import * as Sentry from "@sentry/vue"
import { logout } from "~/plugins/auth"
import type { NuxtError } from "#app"

const props = defineProps<{
  error: NuxtError
}>()

const { t } = useI18n()

const { statusUrl } = useAppConfig()

const config = useRuntimeConfig()
const auth0 = useAuth0()

// Always offer a way out of a broken login state (e.g. stale login hints or tokens in the local
// storage): logging out clears them. Not in local dev, where Auth0 is not configured.
const isAuth0Configured = R.values(config.public.auth0).every((e) => R.isTruthy(e))

// Log error to Sentry
Sentry.captureException(props.error)

const is404 = computed(() => props.error.statusCode === 404)
const isUnauthorized = computed(() => [401, 403].includes(props.error.statusCode ?? 0))

function goHome() {
  clearError({ redirect: "/" })
}
</script>

<template>
  <div
    class="flex h-screen w-screen flex-col items-center justify-center bg-gradient-to-b from-sky-100 to-white"
  >
    <div class="max-w-2xl rounded-lg bg-white p-12 shadow-lg">
      <NuxtImg
        provider="imagekit"
        class="mx-auto mb-8 pr-4"
        width="200"
        src="/zefire-spa/logo_renewex.svg"
      />

      <div class="mb-8 flex flex-col items-center gap-y-6 text-center">
        <!-- Animated wind turbine for 404 -->
        <div v-if="is404" class="flex flex-col items-center gap-y-4">
          <ErrorWindTurbine class="text-rnxblue-500" />
          <h1 class="text-3xl font-bold text-gray-900">{{ t("title404") }}</h1>
        </div>

        <!-- Animated eclipse for unauthorized (401/403) -->
        <div v-else-if="isUnauthorized" class="flex flex-col items-center gap-y-4">
          <ErrorSolarEclipse class="text-rnxorange-500" />
          <h1 class="text-3xl font-bold text-gray-900">{{ t("titleUnauthorized") }}</h1>
        </div>

        <!-- Warning icon for other errors -->
        <div v-else class="flex flex-row items-center gap-x-4">
          <UIcon name="i-heroicons-exclamation-triangle" class="size-16 text-rnxorange-500" />
          <h1 class="text-3xl font-bold text-gray-900">{{ t("title") }}</h1>
        </div>

        <div class="flex flex-col gap-y-3">
          <p class="text-lg text-gray-700">
            {{ is404 ? t("msg404") : isUnauthorized ? t("msgUnauthorized") : t("msg") }}
          </p>
          <p v-if="!is404 && !isUnauthorized" class="text-base text-gray-600">{{ t("msg2") }}</p>
        </div>
      </div>

      <div class="flex flex-col items-center gap-y-4">
        <!-- Go Home button for 404 -->
        <UButton v-if="is404" color="primary" size="lg" @click="goHome">
          {{ t("goHome") }}
        </UButton>

        <UButton
          v-if="isAuth0Configured"
          color="gray"
          variant="outline"
          icon="i-mdi-logout"
          @click="() => logout(auth0)"
        >
          {{ t("logout") }}
        </UButton>

        <div class="flex flex-row items-center gap-x-2 text-sm text-gray-600">
          <p>{{ t("status") }}</p>
          <ULink :to="statusUrl" target="_blank" class="font-semibold text-rnxblue-500">
            {{ statusUrl }}
          </ULink>
        </div>

        <div class="flex flex-row items-center gap-x-2 text-sm text-gray-600">
          <p>{{ t("help") }}</p>
          <ULink to="mailto:support@renewex.co" class="font-semibold text-rnxblue-500">
            support@renewex.co
          </ULink>
        </div>

        <div v-if="error.statusCode || error.message" class="text-sm text-gray-400">
          <span v-if="error.statusCode">{{ t("errorCode") }}: {{ error.statusCode }}</span>
          <span v-if="error.statusCode && error.message"> • </span>
          <span v-if="error.message">{{ error.message }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  title: "Something went wrong"
  title404: "Lost in the wind..."
  titleUnauthorized: "Access eclipsed..."
  msg: "We're sorry, but an unexpected error occurred."
  msg404: "The page you're looking for seems to have blown away."
  msgUnauthorized: "The sun hasn't risen on this feature for you yet. Request access from Renewex."
  msg2: "Our team at Renewex has been automatically notified of this issue and is working to resolve it as quickly as possible."
  status: "To get information on current incidents, check:"
  help: "If you need assistance, please contact:"
  errorCode: "Error code"
  goHome: "Back Home"
  logout: "Log out"
fr-FR:
  title: "Une erreur est survenue"
  title404: "Perdu dans le vent..."
  titleUnauthorized: "Accès éclipsé..."
  msg: "Nous sommes désolés, une erreur inattendue s'est produite."
  msg404: "La page que vous recherchez semble s'être envolée."
  msgUnauthorized: "Vous n'avez pas les droits nécessaires pour cette zone. Demandez l'accès à Renewex."
  msg2: "L'équipe Renewex a été notifiée et nous travaillons pour résoudre ce problème au plus vite."
  status: "Pour connaître les incidents en cours, consultez :"
  help: "Si vous avez besoin d'aide, vous pouvez contacter :"
  errorCode: "Code d'erreur"
  goHome: "Retour"
  logout: "Se déconnecter"
</i18n>
